import React, { useState } from "react";
import Table from "../../../../common/element/Table";
import Button from "../../../../common/element/Buttons";
import CommonIconButton from "../../../../common/icons/CommonIconButton";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import AddEditSupportedDocument from "./AddEdit";
import ArchiveModal from "../../../../common/Common/ArchiveModal";
import moment from "moment/moment";
import { selectUserData } from "../../../../redux/auth/authSelector";
import { ROLES } from "../../../../config/rolePermissions";
import DocumentViewerModal from "./EmployeeView";
import ThreeDotBouncingLoader from "../../../../common/element/ButtonLoader/ThreeDotBouncingLoder";
import { archiveSupportedDocument, clearArchiveSupportedDocument } from "../../../../redux/SupportedDocuments/supportedDocumentsSlice";
import SweetAlertService from "../../../../common/SweetAlert/SweetAlert";

const { Tr, Th, Td, THead, TBody } = Table;

const SupportedDocumentsList = ({ data = [], onRefresh, currentPage, pageLimit, loading }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [mode, setMode] = useState('add');
  const [isOpen, setIsOpen] = useState(false);
  const [isDocumentOpen, setIsDocumentOpen] = useState(false);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [documentToArchive, setDocumentToArchive] = useState(null);

  const userData = useSelector(selectUserData);
  const userRole = userData?.role_name;

  // Transform employee documents data to match admin format
  const transformedData = React.useMemo(() => {
    if (userRole === ROLES.ADMIN) {
      return data;
    }

    // Transform employee documents - extract from data property
    const employeeData = data?.data || data;
    if (!Array.isArray(employeeData)) return [];

    return employeeData.map(item => ({
      supported_doc_id: item.supported_doc_id,
      id: item.id,
      documentName: item.supportedDocument?.name || "N/A",
      name: item.supportedDocument?.name || "N/A",
      sessionName: item.companySession?.session_name || "N/A",
      created_at: item.created_at,
      createdOn: item.created_at,
      documents: item.supportedDocument?.documents || [],
      is_archive: item.is_archived, // Ensure this exists
      // Store original item for document viewer
      _originalItem: item
    }));
  }, [data, userRole]);

  const handleView = (item) => {
    setSelectedDocument(item);
    setMode('view');
    setDialogOpen(true);
  };

  const handleEdit = (item) => {
    setSelectedDocument(item);
    setMode('edit');
    setDialogOpen(true);
  };

  // Updated to handle both Archive and Unarchive
  const handleArchiveClick = (item) => {
    setDocumentToArchive(item);
    setIsOpen(true);
  };

  const handleArchiveConfirm = async () => {
    if (!documentToArchive) return;

    const documentId = documentToArchive.supported_doc_id || documentToArchive.id;
    // Determine if we are archiving or unarchiving based on current state
    const isArchiving = !documentToArchive.is_archived;

    setArchiveLoading(true);
    try {
      // Pass object with id and is_archive status
      const result = await dispatch(archiveSupportedDocument({
        id: documentId,
        is_archive: isArchiving
      })).unwrap();

      setIsOpen(false);
      setDocumentToArchive(null);

      SweetAlertService.showSuccess({
        title: 'Success!',
        message: result?.message || `Supported document has been ${isArchiving ? 'archived' : 'unarchived'} successfully.`,
        onConfirm: () => {
          dispatch(clearArchiveSupportedDocument());
          if (typeof onRefresh === 'function') {
            onRefresh();
          }
        }
      });

    } catch (error) {
      console.error("Failed to archive document:", error);

      SweetAlertService.showError({
        title: 'Error!',
        message: error || `Failed to ${isArchiving ? 'archive' : 'unarchive'} document. Please try again.`,
      });
    } finally {
      setArchiveLoading(false);
    }
  };

  const handleArchiveClose = () => {
    setIsOpen(false);
    setDocumentToArchive(null);
  };

  const handleEmployeeDocumentView = (item) => {
    setSelectedDocument(item._originalItem || item);
    setIsDocumentOpen(true);
  };

  return (
    <>
      <div className="border rounded-lg border-gray-200">
        <Table>
          <THead>
            <Tr className="text-start">
              <Th className="text-start">S No.</Th>
              <Th className="text-start">Name of Document</Th>

              {userRole === ROLES.ADMIN ? (
                <Th className="text-start">Access For</Th>
              ) : (
                <Th className="text-start">Session Name</Th>
              )}

              <Th className="text-start">Created on</Th>
              {/* Added Status Column for Admin */}
              {userRole === ROLES.ADMIN && <Th className="text-start">Status</Th>}
              <Th className="text-start">Action</Th>
            </Tr>
          </THead>

          <TBody>
            {loading ? (
              <Tr>
                <Td colSpan={userRole === ROLES.ADMIN ? 6 : 5} className="py-10 text-center">
                  <ThreeDotBouncingLoader />
                </Td>
              </Tr>
            ) : transformedData.length > 0 ? (
              transformedData.map((item, index) => (
                <Tr key={item.supported_doc_id || item.id} className="text-start">
                  <Td>
                    {String((currentPage - 1) * pageLimit + (index + 1)).padStart(2, "0")}
                  </Td>

                  <Td className="text-start">{item.documentName || item.name || ""}</Td>

                  <Td>
                    {userRole === ROLES.ADMIN ? (
                      item.access_for && item.access_for.length > 0 ? (
                        item.access_for.some(r => r.access_group_name === "All")
                          ? "Management, Employee"
                          : item.access_for
                            .filter(r => r.access_group_name !== "All")
                            .map(r => r.access_group_name)
                            .join(", ") || "N/A"
                      ) : (
                        "N/A"
                      )
                    ) : (
                      item.sessionName || "N/A"
                    )}
                  </Td>

                  <Td className="text-start">
                    {item.createdOn
                      ? moment(item.createdOn).format("LLL")
                      : item.created_at
                        ? moment(item.created_at).format("LLL")
                        : ""}
                  </Td>

                  {/* Status Column for Admin */}
                  {userRole === ROLES.ADMIN && (
                    <Td className="text-start">
                      <span
                        className={`inline-block px-3 py-1 text-sm font-medium rounded-full capitalize ${item.is_archived
                          ? "bg-slate-100 text-slate-700"
                          : "bg-green-100 text-green-800"
                          }`}
                      >
                        {item.is_archived ? "Archived" : "Active"}
                      </span>
                    </Td>
                  )}

                  <Td className="text-start">
                    <div className="flex gap-1">
                      {/* EMPLOYEE/HR ROLE → Show Only View Button */}
                      {(userRole === ROLES.EMPLOYEE || userRole === ROLES.HR || userRole === ROLES.COMPANYADMIN || userRole === ROLES.MANAGEMENT || userRole === ROLES.SCIENCEOFSELF || userRole === ROLES.SALESTRAININGCOMPANY) && (
                        <CommonIconButton
                          type="view"
                          onClick={() => handleEmployeeDocumentView(item)}
                        />
                      )}

                      {/* ADMIN ROLE → VIEW, EDIT, ARCHIVE/UNARCHIVE */}
                      {userRole === ROLES.ADMIN && (
                        <>
                          <CommonIconButton
                            type="view"
                            onClick={() => handleView(item)}
                          />
                          {/* Hide Edit button if archived */}
                          {!item.is_archived && (
                            <CommonIconButton
                              type="edit"
                              onClick={() => handleEdit(item)}
                            />
                          )}
                          {!item.is_archived ? 
                          <CommonIconButton
                            type="archive"
                            onClick={() => handleArchiveClick(item)}

                          /> : <CommonIconButton
                            type="unarchive"
                            onClick={() => handleArchiveClick(item)}
                        
                          />}

                        </>
                      )}
                    </div>
                  </Td>
                </Tr>
              ))
            ) : (
              <Tr>
                <Td colSpan={userRole === ROLES.ADMIN ? 6 : 5} className="text-center text-gray-500 py-6 text-sm">
                  No matching records found
                </Td>
              </Tr>
            )}
          </TBody>
        </Table>
      </div>

      {userRole === ROLES.ADMIN && (
        <AddEditSupportedDocument
          dialogOpen={dialogOpen}
          onDialogClose={() => {
            setDialogOpen(false);
            setSelectedDocument(null);
          }}
          documentData={selectedDocument}
          mode={mode}
          onAdded={() => { if (typeof onRefresh === 'function') onRefresh(); }}
        />
      )}

      {userRole === ROLES.ADMIN && (
        <ArchiveModal
          dialogOpen={isOpen}
          onDialogClose={handleArchiveClose}
          onConfirm={handleArchiveConfirm}
          loading={archiveLoading}
          title={documentToArchive?.is_archived == true
            ? `Are you sure you want to unarchive "${documentToArchive?.name}" Document ?`
            : `Are you sure you want to archive "${documentToArchive?.name}" Document`}

        />
      )}

      <DocumentViewerModal
        isOpen={isDocumentOpen}
        setIsOpen={setIsDocumentOpen}
        documentData={selectedDocument}
      />
    </>
  );
};

export default SupportedDocumentsList;